/**
 * AntD v5 theme for the clinic panel and the super-admin panel.
 *
 * ```tsx
 * import { ConfigProvider } from 'antd';
 * import faIR from 'antd/locale/fa_IR';
 * import { bookoraAntdTheme } from '@bookora/design/antd';
 *
 * <ConfigProvider direction="rtl" locale={faIR} theme={bookoraAntdTheme(mode)}>
 * ```
 *
 * The goal is that neither panel reads as "default AntD with a different blue".
 * Four things do most of that work:
 *
 * 1. **Density.** Controls are 36px, table cells are tighter, and form items
 *    have less bottom margin — these are tools a receptionist lives in for
 *    eight hours, and AntD's stock rhythm wastes a row of patients per screen.
 *    **The marketing scale never reaches this file**: `sectionSpacing` and
 *    `sectionSpacingLoose` are not imported, and nothing here is above 32px.
 * 2. **Shape, inverted per theme.** Every radius comes from `semanticRadii`,
 *    so a control is 4px in Editorial Coral and a full pill in Midnight Coral
 *    without either panel knowing which theme it is in.
 * 3. **Elevation.** In light, AntD's blurred drop shadows are replaced by the
 *    hard blur-free ink offsets — that single substitution is most of what
 *    makes the panel read as print. In dark they become the inset frost stack.
 * 4. **Interaction direction — and it is no longer one direction.** AntD
 *    lightens on hover in both themes. This theme lightens in light and
 *    **darkens in dark**, which is not a compromise: the primary is achromatic
 *    at the extreme of the ramp in each theme (ink on paper, paper on ink), so
 *    the only move available is *toward the canvas*. That is the rule; the
 *    direction is a consequence of which end the fill starts from.
 *
 *    The reason has now inverted twice, and the numbers are worth keeping so
 *    the next person does not restore a rule from memory. Under the old muted
 *    rose the label was white and lightening dropped it under 4.5:1, so the
 *    theme darkened. Under the vivid pink the label was Ink and lightening
 *    washed the fill out past the 3:1 UI-object floor, so it still darkened —
 *    same conclusion, different reason. Now contrast has stopped constraining
 *    it at all: the light ladder runs 14.74:1 → 12.45:1 → 10.53:1 and the dark
 *    one 14.74:1 → 11.95:1 → 9.49:1, both an order of magnitude clear of every
 *    floor. What bounds them is identity — three steps is as far as an ink fill
 *    can travel before it reads as a disabled grey chip.
 *
 * ## Density, and how the numbers below were arrived at
 *
 * These were carried over unvalidated from the pre-re-skin theme until the
 * super-admin console's **Tenants table** gave the platform its first genuinely
 * dense screen to measure: 10 columns with 2–3 stacked lines per row (14px at
 * 1.7 line-height plus 0.6875rem secondaries), at AntD's default size.
 *
 * At `cellPaddingBlock: 12` that renders ~67px rows — about 8–9 visible on a
 * 1080p screen against a 25-row page, so most of a page needs scrolling to
 * count. **10 buys roughly one row back and 8 buys two**, and 10 is where the
 * stacked secondary line still has air above and below it. 36px controls and
 * 40px menu items measured fine and are unchanged.
 *
 * The number lives here rather than as a `size="middle"` default on the panels'
 * `DataTable`, so that both panels stay identical and every table still being
 * built — the clinic panel's patient list, calendar and inventory — inherits it
 * without having to know.
 *
 * Still unmeasured: the 20px form-item margin. The densest real *form* has not
 * been built yet; when it is, it should be checked the same way.
 *
 * `cssVar` is on, so AntD emits its own custom properties and switching themes
 * is a variable swap rather than a full style regeneration.
 */
import { theme } from "antd";
import { breakpoints, colors, control, neutral, motion, radii, semanticRadii, shadows, spacing, typography, zIndex, } from "./tokens.js";
/** `'160ms'` → `'0.16s'`. AntD composes these into `transition` shorthands. */
function seconds(ms) {
    return `${Number.parseInt(ms, 10) / 1000}s`;
}
/** `'12px'` → `12`. AntD's token values are unitless numbers. */
function px(value) {
    return Number.parseFloat(value);
}
/** `'0.875rem'` → `14`. */
function rem(value) {
    return Math.round(Number.parseFloat(value) * 16);
}
/**
 * AntD's eighteen breakpoint seed tokens, derived from the six in `breakpoints`.
 *
 * The `Max` of a step is the next step minus one pixel — AntD's own
 * relationship between them (`screenXSMax: 575` under `screenSM: 576`) — and
 * the largest step has no `Max`, because there is nothing above it.
 */
function antdBreakpoints() {
    const px = (value) => Number.parseFloat(value);
    const steps = [
        ["XS", px(breakpoints.xs)],
        ["SM", px(breakpoints.sm)],
        ["MD", px(breakpoints.md)],
        ["LG", px(breakpoints.lg)],
        ["XL", px(breakpoints.xl)],
        ["XXL", px(breakpoints["2xl"])],
    ];
    const out = {};
    steps.forEach(([name, value], index) => {
        out[`screen${name}`] = value;
        out[`screen${name}Min`] = value;
        const next = steps[index + 1];
        if (next)
            out[`screen${name}Max`] = next[1] - 1;
    });
    return out;
}
export function bookoraAntdTheme(mode = "light") {
    const t = colors[mode];
    const elevation = shadows[mode];
    const shape = semanticRadii[mode];
    const dark = mode === "dark";
    /**
     * The resting elevation of an interactive control. In light this is the hard
     * 2px ink plate; in dark it is nothing, because a pill button is lifted by
     * its own frost edge and a drop shadow under it would read as grime.
     *
     * AntD has no hover-only shadow token — `primaryShadow` and friends are
     * static — so the plate is drawn at rest, which is also what the reference
     * does. Components that want the press-in behaviour translate on :active and
     * drop to `--bk-shadow-none`; that is a panel-local rule, not a token.
     */
    const controlShadow = dark ? "none" : elevation.sm;
    /**
     * ── The `colorTextLightSolid` trap ────────────────────────────────────────
     *
     * AntD has exactly ONE global token for "text on a solid fill", and it feeds
     * every solid surface in the library: the primary button, the danger button,
     * the count badge, preset-coloured tags, the error step icon, the danger
     * dropdown item's hover, the image-preview toolbar, the spin overlay.
     *
     * Under a dark primary that was harmless — one white answered for all of
     * them. Under the vivid pink it was not: `onPrimary` became **Ink**, because
     * white on `#FF4790` is 2.9:1. Setting `colorTextLightSolid: t.onPrimary` is
     * right for the primary fills and silently wrong for everything else — an ink
     * label on a crimson delete button was 2.2:1, and it is invisible in a diff.
     *
     * **The logo re-skin moved this trap into dark mode; it did not remove it.**
     * `onPrimary` inverted, so light is now paper — which is AntD's own default,
     * and white on the light crimson fill is 8.72:1, fine by coincidence. Dark is
     * now Ink, and Ink on `crimson[400]` is **3.37:1**. Exactly one theme needs
     * these overrides at any given time and which one it is has changed once
     * already, so nothing below may be narrowed to a single mode.
     *
     * So the global stays `onPrimary` (the majority case) and every surface whose
     * fill is NOT the primary gets its on-colour stated below. Three grounds, three
     * answers:
     */
    /** Our own status fills. Theme-aware, and already contrast-tested as pairs. */
    const onDanger = t.onDanger;
    /**
     * Grounds that are NOT ours: AntD's own preset palette (`<Tag color="red">`,
     * `<Button color="volcano">`) and the literal black scrims behind the image
     * preview, the upload picture-card and the spin overlay. AntD ships white on
     * all of them in both its themes and it is right — these are saturated
     * mid-tones and 50%-black washes, not Bookora surfaces, so a Bookora token
     * would be answering a question about a colour it does not own.
     */
    const onForeignSolid = neutral[0];
    /**
     * The Avatar fallback ground, which is `colorTextPlaceholder` — i.e. our own
     * `fgSubtle`, and that flips across the themes rather than staying dark.
     * White on light `fgSubtle` (#6A6A6A) is 5.41:1 and ink is 2.73:1; in dark
     * (#AFAAAA) it is the other way round — white 2.29:1, `neutral[975]` 8.58:1.
     * One value cannot serve both.
     */
    const onSubtleFill = dark ? neutral[975] : neutral[0];
    return {
        algorithm: dark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        cssVar: { key: "bookora" },
        hashed: false,
        token: {
            /* --- seeds ---------------------------------------------------- */
            colorPrimary: t.primary,
            colorSuccess: t.success,
            colorWarning: t.warning,
            colorError: t.danger,
            colorInfo: t.info,
            colorTextBase: t.fg,
            colorBgBase: t.bg,
            // ── OPEN: an inline link is now invisible until hovered ──────────────
            //
            // `primaryFg` is the correct token — a link is the primary role rendered
            // as text — but the primary went achromatic with the logo re-skin, so
            // `primaryFg` and `fg` are the **same colour**: 1.00:1 in light
            // (both #282828) and 1.11:1 in dark. AntD renders `<a>` with no
            // underline, so a link inside a paragraph in either panel is now
            // indistinguishable from the sentence around it.
            //
            // That is WCAG 2.1 §1.4.1. Colour is the only cue, and the guidance for
            // colour-only link identification is 3:1 against the surrounding text
            // *plus* a non-colour cue on hover and focus. This has neither.
            //
            // BOTH halves are applied, because neither is sufficient alone:
            //
            //   1. `colorLink` points at `accentFg` (coral[700]). This restores the
            //      "that word is a link" affordance that an achromatic primary threw
            //      away. It is 5.97:1 on paper, so it is legible as text — but only
            //      2.47:1 against the body ink beside it, under the 3:1 that
            //      colour-only link identification needs. So it is not enough.
            //   2. A PERSISTENT underline (`Typography.linkDecoration` below, and
            //      `underline` rather than `hover:underline` on the `link` variant in
            //      `recipes.ts`). This is the non-colour cue §1.4.1 actually asks
            //      for, and it is the half that carries the requirement.
            //
            // Spending the accent here is a deliberate exception to "coral is
            // punctuation, never a fill": an inline link IS punctuation — a few words
            // inside a paragraph — and it is the one place the reader must be able to
            // find without being told.
            //
            // `colorLink` and the `link` recipe must move together or the panels and
            // the marketing site will disagree.
            colorLink: t.accentFg,
            /* --- primary family ------------------------------------------- */
            // Explicit rather than derived: AntD's generator lightens on hover, which
            // fails AA against `onPrimary` on this ramp.
            colorPrimaryHover: t.primaryHover,
            colorPrimaryActive: t.primaryActive,
            colorPrimaryBorder: t.primaryBorder,
            colorPrimaryBorderHover: t.primary,
            colorPrimaryBg: t.primarySoft,
            colorPrimaryBgHover: t.primarySoftHover,
            colorPrimaryText: t.primaryFg,
            colorPrimaryTextHover: t.primaryHover,
            colorPrimaryTextActive: t.primaryActive,
            // The accent's own darker steps, so hover/active deepen the coral rather
            // than jumping to the achromatic primary ladder.
            colorLinkHover: t.accentStrongHover,
            colorLinkActive: t.accentStrong,
            /* --- status families ------------------------------------------ */
            colorSuccessBg: t.successSoft,
            colorSuccessBgHover: t.successSoft,
            colorSuccessBorder: t.successBorder,
            colorSuccessBorderHover: t.success,
            colorSuccessText: t.successFg,
            colorSuccessTextHover: t.successHover,
            colorSuccessTextActive: t.successHover,
            colorSuccessHover: t.successHover,
            colorSuccessActive: t.successHover,
            colorWarningBg: t.warningSoft,
            colorWarningBgHover: t.warningSoft,
            colorWarningBorder: t.warningBorder,
            colorWarningBorderHover: t.warning,
            colorWarningText: t.warningFg,
            colorWarningTextHover: t.warningHover,
            colorWarningTextActive: t.warningHover,
            colorWarningHover: t.warningStrong,
            colorWarningActive: t.warningHover,
            colorErrorBg: t.dangerSoft,
            colorErrorBgHover: t.dangerSoft,
            colorErrorBorder: t.dangerBorder,
            colorErrorBorderHover: t.danger,
            colorErrorText: t.dangerFg,
            colorErrorTextHover: t.dangerHover,
            colorErrorTextActive: t.dangerHover,
            colorErrorHover: t.dangerHover,
            colorErrorActive: t.dangerHover,
            colorInfoBg: t.infoSoft,
            colorInfoBorder: t.infoBorder,
            colorInfoText: t.infoFg,
            colorInfoHover: t.infoHover,
            colorInfoActive: t.infoHover,
            /* --- text ------------------------------------------------------ */
            colorText: t.fg,
            colorTextSecondary: t.fgMuted,
            colorTextTertiary: t.fgSubtle,
            colorTextQuaternary: t.fgDisabled,
            colorTextDescription: t.fgSubtle,
            colorTextDisabled: t.fgDisabled,
            colorTextPlaceholder: t.fgSubtle,
            colorTextHeading: t.fg,
            colorTextLabel: t.fgMuted,
            colorTextLightSolid: t.onPrimary,
            colorIcon: t.fgSubtle,
            colorIconHover: t.fg,
            /* --- surfaces --------------------------------------------------- */
            colorBgContainer: t.bgElevated,
            colorBgElevated: t.bgElevated,
            colorBgLayout: t.bg,
            colorBgSpotlight: dark ? t.bgMuted : t.fg,
            colorBgMask: t.overlay,
            colorBgBlur: "transparent",
            colorFillAlter: t.bgSubtle,
            colorFillContent: t.bgMuted,
            colorFillContentHover: t.bgHover,
            colorFillSecondary: t.bgHover,
            colorFillTertiary: t.bgSubtle,
            colorFillQuaternary: t.bgSubtle,
            /* --- borders ----------------------------------------------------- */
            colorBorder: t.borderStrong,
            colorBorderSecondary: t.border,
            colorSplit: t.borderSubtle,
            lineWidth: px(control.borderWidth),
            lineType: "solid",
            /* --- typography --------------------------------------------------- */
            fontFamily: typography.fontFamily.body,
            fontFamilyCode: typography.fontFamily.mono,
            fontSize: rem(typography.scale.body.fontSize),
            fontSizeSM: rem(typography.scale["body-sm"].fontSize),
            fontSizeLG: rem(typography.scale["body-lg"].fontSize),
            fontSizeXL: rem(typography.scale.title.fontSize),
            fontSizeHeading1: rem(typography.scale.display.fontSize),
            fontSizeHeading2: rem(typography.scale["heading-lg"].fontSize),
            fontSizeHeading3: rem(typography.scale.heading.fontSize),
            fontSizeHeading4: rem(typography.scale["heading-sm"].fontSize),
            fontSizeHeading5: rem(typography.scale["title-sm"].fontSize),
            // Persian needs the room. AntD's stock 1.5714 crowds the dots under
            // ب/پ/ی into the line below in a dense table.
            lineHeight: Number(typography.scale.body.lineHeight),
            lineHeightSM: Number(typography.scale["body-sm"].lineHeight),
            lineHeightLG: Number(typography.scale["body-lg"].lineHeight),
            lineHeightHeading1: Number(typography.scale.display.lineHeight),
            lineHeightHeading2: Number(typography.scale["heading-lg"].lineHeight),
            lineHeightHeading3: Number(typography.scale.heading.lineHeight),
            lineHeightHeading4: Number(typography.scale["heading-sm"].lineHeight),
            lineHeightHeading5: Number(typography.scale["title-sm"].lineHeight),
            // AntD's stock 600 lands between Yekan Bakh's Regular and SemiBold cuts.
            // The weight ranges in fonts.css resolve it to SemiBold, which is the
            // face a bold table header or a <strong> should be using.
            fontWeightStrong: typography.fontWeight.semibold,
            /* --- shape --------------------------------------------------------- */
            // Theme-resolved, not fixed: 4px inputs and 8px cards in light; 6px
            // inputs and 16px cards in dark. `borderRadius` is AntD's base step and
            // most components derive from it, so it is the *input* radius rather than
            // the card one — a control must never out-round its container.
            borderRadius: px(shape.input),
            borderRadiusXS: dark ? 4 : 2,
            borderRadiusSM: px(shape.input),
            borderRadiusLG: px(shape.card),
            borderRadiusOuter: dark ? 4 : 2,
            /* --- density ------------------------------------------------------- */
            controlHeight: px(control.height.md),
            controlHeightSM: px(control.height.sm),
            controlHeightLG: px(control.height.lg),
            controlHeightXS: 22,
            sizeUnit: 4,
            sizeStep: 4,
            sizePopupArrow: 14,
            padding: px(spacing[4]),
            paddingXS: px(spacing[2]),
            paddingSM: px(spacing[3]),
            paddingLG: px(spacing[6]),
            paddingXL: px(spacing[8]),
            margin: px(spacing[4]),
            marginXS: px(spacing[2]),
            marginSM: px(spacing[3]),
            marginLG: px(spacing[6]),
            marginXL: px(spacing[8]),
            /* --- elevation ------------------------------------------------------ */
            // AntD's stock shadows are blurred neutral black. In light they are
            // replaced wholesale by the hard, blur-free ink offsets — there are no
            // blurred drop shadows anywhere in Editorial Coral. In dark they become
            // the inset-frost + halo stack. Both come from `shadows[mode]`, so a
            // panel that spells `boxShadow` gets the right personality for free.
            boxShadow: elevation.md,
            boxShadowSecondary: elevation.lg,
            boxShadowTertiary: elevation.sm,
            /* --- focus ---------------------------------------------------------- */
            // THE CANONICAL FOCUS TREATMENT, in AntD's vocabulary. AntD composes its
            // focus indicator as `box-shadow: 0 0 0 <controlOutlineWidth>
            // <controlOutline>`, so those two tokens ARE the ring — and every
            // component's `activeShadow` is the same ring restated.
            //
            // `controlOutline` used to be `rgba(214, 47, 118, 0.32)`, a translucent
            // glow that composites to 1.63:1 on the paper canvas and 1.36:1 on the
            // dark one. That FAILS WCAG 2.1 §1.4.11's 3:1 for a focus indicator.
            // It looked compliant only because `activeBorderColor` turned the border
            // pink at the same moment — the doubled halo users reported was the half
            // that was actually doing the work.
            //
            // Now: a SOLID `ring`, 2px — achromatic in both themes (neutral[700] /
            // neutral[300]; a coral ring made a focused, empty field look invalid),
            // clearing 3:1 on every surface. And because the ring is
            // now the whole indicator, `activeBorderColor` on every input-like
            // component below is pinned to `borderStrong` — the border does not change
            // on focus.
            controlOutline: t.ring,
            controlOutlineWidth: px(control.focusRingWidth),
            controlTmpOutline: t.bgHover,
            // ── AntD has TWO focus code paths, and the tokens above only drive one ──
            //
            // `controlOutline`/`controlOutlineWidth` become the `box-shadow` ring on
            // the INPUT family. Buttons — and everything else that goes through
            // `genFocusStyle()` in antd/lib/style/index.js — draw a real CSS
            // `outline` from a completely different pair:
            //
            //   outline: <lineWidthFocus> solid <colorPrimaryBorder>;
            //   outline-offset: 1;                      // hardcoded literal
            //
            // Neither was set here, so buttons kept AntD's defaults and the product
            // shipped three different focus geometries at once: the Astro site's 2px
            // outline at 2px offset, the AntD input's flush 2px ring, and the AntD
            // button's 3px outline at 1px offset in a colour that is not the ring.
            //
            // Worse than inconsistent — that default is not compliant. The button
            // ring was `colorPrimaryBorder`, which measures **1.84:1 in light and
            // 2.05:1 in dark** against the canvas, against a §1.4.11 floor of 3:1.
            // Every button in both panels had a focus indicator that failed.
            //
            // `lineWidthFocus` is the half a token CAN fix, so it is fixed here and
            // becomes the same 2px single source as everything else.
            //
            // The other two halves cannot be reached from the theme:
            //   - the COLOUR comes from `colorPrimaryBorder`, which is deliberately
            //     NOT the ring — it is the hairline on `primarySoft` grounds (tags,
            //     alerts, the selected-row edge) and repointing it at the ring would
            //     repaint all of those to fix a focus outline;
            //   - the OFFSET is a hardcoded `1` in AntD's own style function, with no
            //     token behind it at all.
            // Both are corrected in `packages/ui/src/styles/ui.css`, which is the
            // same file that already suppresses the double ring on wrapped inputs.
            lineWidthFocus: px(control.focusRingWidth),
            /* --- motion --------------------------------------------------------- */
            motionDurationFast: seconds(motion.duration.fast),
            motionDurationMid: seconds(motion.duration.base),
            motionDurationSlow: seconds(motion.duration.slow),
            motionEaseOut: motion.easing.out,
            motionEaseInOut: motion.easing.inOut,
            motionEaseOutCirc: motion.easing.out,
            motionEaseInOutCirc: motion.easing.inOut,
            motionEaseOutQuint: motion.easing.out,
            motionEaseInQuint: motion.easing.in,
            // AntD's defaults for these two overshoot (y2 of 1.46 on the way out):
            // the checkbox tick, the badge count and the form error message all
            // bounce. The panels carry no bounce, so both are pinned to the plain
            // curves.
            motionEaseOutBack: motion.easing.out,
            motionEaseInBack: motion.easing.in,
            /* --- layering ------------------------------------------------------- */
            zIndexBase: zIndex.base,
            zIndexPopupBase: zIndex.modal,
            /* --- breakpoints ---------------------------------------------------- */
            // The `*Min`/`*Max` companions are NOT derived by AntD from the base
            // values — they are separate seed tokens with their own defaults, and
            // AntD asserts `screenXXLMin <= screenXXL` at render time. Overriding
            // `screenXXL` to 1440 while leaving `screenXXLMin` at its default 1600
            // throws inside `Descriptions`, `Row` and anything else that calls
            // `useBreakpoint`. Every stop is therefore stated.
            //
            // …but READ FROM `breakpoints`, not retyped. This block used to spell the
            // six numbers out, which made AntD the only one of the three emissions
            // not bound to the token: `tailwind-preset.ts` and `css-vars.ts` both
            // read them by name. Changing `breakpoints.md` would have moved the
            // patient PWA's `md:` and `--bk-bp-md` and left a clinic-panel
            // `<Col md>` breaking at the old width — the one outcome the Tailwind
            // preset's own comment says must never happen.
            ...antdBreakpoints(),
            // Flat surfaces with hairline borders, not AntD's wireframe mode.
            wireframe: false,
            opacityImage: 1,
        },
        components: {
            /* ================================================================ */
            /*  Typography                                                      */
            /* ================================================================ */
            Typography: {
                // The non-colour cue for inline links — see `colorLink` above. AntD
                // renders `<a>` undecorated by default, which was fine while the
                // primary was a vivid pink and is not fine now that it is ink.
                linkDecoration: "underline",
                linkHoverDecoration: "underline",
            },
            /* ================================================================ */
            /*  Button                                                          */
            /* ================================================================ */
            Button: {
                fontWeight: typography.fontWeight.medium,
                // 4px in light, a full pill in dark. The inversion is the token's, not
                // this file's — see `semanticRadii` in tokens.ts.
                borderRadius: px(shape.control),
                borderRadiusSM: px(shape.control),
                borderRadiusLG: px(shape.control),
                controlHeight: px(control.height.md),
                controlHeightSM: px(control.height.sm),
                controlHeightLG: px(control.height.lg),
                // The canonical control size scale, not the type scale. A button and a
                // text field of the same nominal size must be the same object, and the
                // only way that holds is that both read `control`.
                paddingInline: px(control.paddingInline.md),
                paddingInlineSM: px(control.paddingInline.sm),
                paddingInlineLG: px(control.paddingInline.lg),
                contentFontSize: rem(control.fontSize.md),
                contentFontSizeLG: rem(control.fontSize.lg),
                contentFontSizeSM: rem(control.fontSize.sm),
                // AntD's own coloured drop shadow is off; what replaces it in light is
                // the hard ink plate, which is the editorial gesture. In dark it is
                // "none" and the frost edge does the lifting.
                primaryShadow: controlShadow,
                defaultShadow: controlShadow,
                dangerShadow: controlShadow,
                // BOTH of these default to `colorTextLightSolid`, i.e. to `onPrimary`,
                // which is paper in light and **Ink in dark**. Ink on the dark crimson
                // fill is 3.37:1, so the danger button still needs its own answer — the
                // theme that needs it moved with the re-skin, the need did not go away.
                // Stated explicitly rather than left to derive: this is the exact
                // regression the on-colour sweep in `antd-theme.test.ts` exists to
                // catch, and it walks both modes for this reason.
                primaryColor: t.onPrimary,
                dangerColor: onDanger,
                defaultBg: t.bgElevated,
                defaultColor: t.fg,
                // 1px solid ink in light. The structural hairline IS the secondary
                // button — there is no fill and no tint until it is hovered.
                defaultBorderColor: t.borderStrong,
                defaultHoverBg: t.bgHover,
                defaultHoverColor: t.primaryFg,
                defaultHoverBorderColor: t.primary,
                defaultActiveBg: t.bgActive,
                defaultActiveColor: t.primaryActive,
                defaultActiveBorderColor: t.primaryActive,
                textHoverBg: t.bgHover,
                colorPrimaryHover: t.primaryHover,
                colorPrimaryActive: t.primaryActive,
                linkHoverBg: "transparent",
                onlyIconSize: 16,
            },
            /* ================================================================ */
            /*  Table — the panel's most-used surface                           */
            /* ================================================================ */
            Table: {
                // The empty-table message is read, not disabled: AntD draws it in
                // `colorTextDisabled` (2.4:1). It takes the subtle text step (AA).
                colorTextDisabled: t.fgSubtle,
                headerBg: t.bgMuted,
                headerColor: t.fgMuted,
                headerSortActiveBg: t.bgActive,
                headerSortHoverBg: t.bgHover,
                headerSplitColor: t.border,
                headerBorderRadius: px(shape.card),
                borderColor: t.borderSubtle,
                rowHoverBg: t.bgHover,
                // A selected patient row is tinted with the brand, not a grey wash —
                // it is the one row the doctor is acting on.
                rowSelectedBg: t.primarySoft,
                rowSelectedHoverBg: t.primarySoftHover,
                rowExpandedBg: t.bgSubtle,
                bodySortBg: t.bgSubtle,
                footerBg: t.bgSubtle,
                footerColor: t.fgMuted,
                // Tighter than AntD's 16/16, and 10 rather than 12 — see the density
                // note in the module docblock. Measured against the super-admin
                // console's Tenants table, the platform's densest real screen.
                cellPaddingBlock: 10,
                cellPaddingInline: 12,
                cellPaddingBlockMD: 10,
                cellPaddingInlineMD: 10,
                cellPaddingBlockSM: 8,
                cellPaddingInlineSM: 8,
                cellFontSize: rem(typography.scale.body.fontSize),
                cellFontSizeMD: rem(typography.scale["body-sm"].fontSize),
                cellFontSizeSM: rem(typography.scale["body-sm"].fontSize),
                selectionColumnWidth: 44,
                expandIconBg: t.bgElevated,
                filterDropdownBg: t.bgElevated,
                filterDropdownMenuBg: t.bgElevated,
                stickyScrollBarBg: t.borderStrong,
                stickyScrollBarBorderRadius: px(radii.pill),
            },
            /* ================================================================ */
            /*  Form                                                            */
            /* ================================================================ */
            Form: {
                labelColor: t.fgMuted,
                labelFontSize: rem(typography.scale["body-sm"].fontSize),
                labelRequiredMarkColor: t.danger,
                labelColonMarginInlineStart: 2,
                labelColonMarginInlineEnd: 8,
                // 20 rather than 24: clinical forms are long, and the label/control
                // pair already carries enough vertical rhythm on its own.
                itemMarginBottom: 20,
                verticalLabelPadding: `0 0 ${spacing[1]}`,
                labelHeight: 22,
            },
            /* ================================================================ */
            /*  Menu — the clinic panel's sider                                 */
            /* ================================================================ */
            Menu: {
                itemBg: "transparent",
                subMenuItemBg: "transparent",
                popupBg: t.bgElevated,
                itemColor: t.fgMuted,
                itemHoverColor: t.fg,
                itemHoverBg: t.bgHover,
                itemSelectedColor: t.primaryFg,
                itemSelectedBg: t.primarySoft,
                itemActiveBg: t.primarySoftHover,
                // A group heading is navigation, not a footnote: 12px subtle grey read
                // as noise beside 14px items. One step down from the items, darker.
                groupTitleColor: t.fgMuted,
                groupTitleFontSize: rem(typography.scale["body-sm"].fontSize),
                itemHeight: 40,
                itemMarginInline: 8,
                itemMarginBlock: 2,
                itemPaddingInline: px(spacing[3]),
                itemBorderRadius: px(shape.control),
                subMenuItemBorderRadius: px(shape.control),
                iconSize: 18,
                collapsedIconSize: 20,
                iconMarginInlineEnd: px(spacing[2.5]),
                // Inline mode's selected marker: off, because the tinted pill already
                // carries the state and the two together look busy.
                activeBarWidth: 0,
                activeBarHeight: 0,
                activeBarBorderWidth: 0,
                horizontalItemSelectedColor: t.primaryFg,
                horizontalItemSelectedBg: "transparent",
                horizontalItemHoverColor: t.primaryFg,
                horizontalItemBorderRadius: px(shape.control),
                // Dark sider: the dark theme's own surfaces, not AntD's stock navy.
                darkItemBg: "transparent",
                darkSubMenuItemBg: "transparent",
                darkPopupBg: colors.dark.bgElevated,
                darkItemColor: colors.dark.fgMuted,
                darkItemHoverColor: colors.dark.fg,
                darkItemHoverBg: colors.dark.bgHover,
                darkItemSelectedColor: colors.dark.primaryFg,
                darkItemSelectedBg: colors.dark.primarySoft,
                darkGroupTitleColor: colors.dark.fgSubtle,
                darkDangerItemSelectedBg: colors.dark.dangerSoft,
            },
            /* ================================================================ */
            /*  Card                                                            */
            /* ================================================================ */
            Card: {
                colorBorderSecondary: t.border,
                headerBg: "transparent",
                headerFontSize: rem(typography.scale["title-sm"].fontSize),
                headerFontSizeSM: rem(typography.scale.body.fontSize),
                headerHeight: 52,
                headerHeightSM: 40,
                actionsBg: t.bgSubtle,
                actionsLiMargin: `${spacing[3]} 0`,
                extraColor: t.primaryFg,
                // 8px in light, 16px in dark — always at least one step above the
                // controls inside the card.
                borderRadiusLG: px(shape.card),
                // A resting card carries NO shadow in light: the brief is explicit that
                // a non-interactive card is 1px of rule on the cream surface and
                // nothing else, and elevation is reserved for what floats or responds.
                // In dark it needs the inset frost or the glass is invisible.
                boxShadowTertiary: dark ? elevation.md : elevation.none,
                paddingLG: px(spacing[6]),
                padding: px(spacing[5]),
                tabsMarginBottom: -px(spacing[1]),
            },
            /* ================================================================ */
            /*  Tag — status pills everywhere in the clinical flow              */
            /* ================================================================ */
            Tag: {
                // `<Tag color="red">` and `<Tag color="…" bordered={false}>` fill from
                // AntD's OWN preset palette, so their label is white in both themes —
                // see `onForeignSolid`. Bookora's own tones (`success`, `processing`, …)
                // are soft grounds with coloured text and are unaffected.
                colorTextLightSolid: onForeignSolid,
                defaultBg: t.bgSubtle,
                defaultColor: t.fgMuted,
                // Pills in light, 6px chips in dark — the tag is the one place light
                // mode is allowed to be fully round.
                borderRadiusSM: px(shape.badge),
                // 12px for every tag, the small size included: 11px Persian was the
                // smallest text in the panel and the hardest to read.
                fontSize: rem(typography.scale.caption.fontSize),
                fontSizeSM: rem(typography.scale.caption.fontSize),
                // A status tag paints its label in the status hue; the raw warning hue
                // is 3.3:1 on its soft ground, under AA for the small text a tag
                // always is. The tag reads each tone's AA «-fg» step instead.
                colorSuccess: t.successFg,
                colorWarning: t.warningFg,
                colorError: t.dangerFg,
                colorInfo: t.infoFg,
                lineHeight: 1.6,
                // Tags carry Persian text; AntD's 7px inline padding crowds it.
                paddingXXS: 3,
                marginXS: px(spacing[1.5]),
            },
            /* ================================================================ */
            /*  Modal                                                           */
            /* ================================================================ */
            Modal: {
                contentBg: t.bgElevated,
                headerBg: t.bgElevated,
                footerBg: "transparent",
                titleColor: t.fg,
                titleFontSize: rem(typography.scale.title.fontSize),
                titleLineHeight: Number(typography.scale.title.lineHeight),
                borderRadiusLG: px(shape.modal),
                boxShadow: elevation.xl,
                padding: px(spacing[6]),
                paddingLG: px(spacing[6]),
                paddingContentHorizontalLG: px(spacing[6]),
                paddingMD: px(spacing[5]),
                colorIcon: t.fgSubtle,
                colorIconHover: t.fg,
            },
            /* ================================================================ */
            /*  DatePicker — Jalali calendar, used on every appointment screen  */
            /* ================================================================ */
            DatePicker: {
                cellHeight: 28,
                cellWidth: 38,
                cellHoverBg: t.bgHover,
                cellActiveWithRangeBg: t.primarySoft,
                cellHoverWithRangeBg: t.primarySoftHover,
                cellRangeBorderColor: t.primaryBorder,
                cellBgDisabled: t.bgSubtle,
                activeBorderColor: t.borderStrong,
                hoverBorderColor: t.primary,
                activeShadow: elevation.focus,
                // The error/warning rings were `dangerSoft` / `warningSoft` — the
                // *banner tint*, i.e. a near-white wash at 1.1:1 against the canvas,
                // which is not a visible ring at all. They are now the solid role
                // colour at the canonical width, and `danger`/`warning` are already
                // contract-tested at 3:1 against `bg` as status marks.
                errorActiveShadow: `0 0 0 ${control.focusRingWidth} ${t.danger}`,
                warningActiveShadow: `0 0 0 ${control.focusRingWidth} ${t.warning}`,
                timeColumnWidth: 60,
                timeColumnHeight: 224,
                timeCellHeight: 28,
                withoutTimeCellHeight: 62,
                presetsWidth: 132,
                presetsMaxWidth: 200,
                paddingBlock: 6,
                controlHeight: px(control.height.md),
                borderRadius: px(shape.input),
                // Today's ring is the brand, not AntD's blue, and the selected day is
                // a solid primary chip.
                colorPrimary: t.primary,
                colorTextHeading: t.fg,
                colorSplit: t.borderSubtle,
            },
            /* ================================================================ */
            /*  Supporting components                                           */
            /* ================================================================ */
            // ── The input family, and the one rule they all obey ────────────────
            // `activeBorderColor` is the FOCUSED border, `hoverBorderColor` is the
            // hovered one. Focus paints the ring and leaves the border alone, so
            // `activeBorderColor` is `borderStrong` — the resting value — on every one
            // of them. Hover keeps its primary tint: hover and focus are different
            // states, and a pointer user hovering a field they have not focused is not
            // being told anything false.
            Input: {
                controlHeight: px(control.height.md),
                controlHeightSM: px(control.height.sm),
                controlHeightLG: px(control.height.lg),
                borderRadius: px(shape.input),
                paddingBlock: 6,
                paddingInline: px(control.paddingInline.md),
                paddingInlineSM: px(control.paddingInline.sm),
                paddingInlineLG: px(control.paddingInline.lg),
                inputFontSize: rem(control.fontSize.md),
                inputFontSizeSM: rem(control.fontSize.sm),
                inputFontSizeLG: rem(control.fontSize.lg),
                activeBorderColor: t.borderStrong,
                hoverBorderColor: t.primary,
                activeShadow: elevation.focus,
                addonBg: t.bgSubtle,
                activeBg: t.bgElevated,
                colorTextPlaceholder: t.fgSubtle,
            },
            InputNumber: {
                controlHeight: px(control.height.md),
                controlHeightSM: px(control.height.sm),
                controlHeightLG: px(control.height.lg),
                borderRadius: px(shape.input),
                paddingInline: px(control.paddingInline.md),
                activeBorderColor: t.borderStrong,
                hoverBorderColor: t.primary,
                activeShadow: elevation.focus,
                handleBg: t.bgSubtle,
                handleHoverColor: t.primary,
                handleBorderColor: t.border,
            },
            Select: {
                controlHeight: px(control.height.md),
                controlHeightSM: px(control.height.sm),
                controlHeightLG: px(control.height.lg),
                borderRadius: px(shape.input),
                optionSelectedBg: t.primarySoft,
                optionSelectedColor: t.primaryFg,
                optionSelectedFontWeight: typography.fontWeight.medium,
                optionActiveBg: t.bgHover,
                optionPadding: `${spacing[1.5]} ${spacing[3]}`,
                optionHeight: 32,
                selectorBg: t.bgElevated,
                multipleItemBg: t.bgMuted,
                multipleItemBorderColor: t.border,
                activeBorderColor: t.borderStrong,
                hoverBorderColor: t.primary,
                activeOutlineColor: t.ring,
            },
            Cascader: {
                optionSelectedBg: t.primarySoft,
                controlItemBgHover: t.bgHover,
            },
            TreeSelect: { nodeSelectedBg: t.primarySoft, nodeHoverBg: t.bgHover },
            Layout: {
                bodyBg: t.bg,
                headerBg: t.bgElevated,
                headerHeight: 60,
                headerPadding: `0 ${spacing[6]}`,
                headerColor: t.fg,
                footerBg: t.bg,
                footerPadding: `${spacing[6]} ${spacing[6]}`,
                siderBg: t.bgElevated,
                triggerBg: t.bgMuted,
                triggerColor: t.fgMuted,
                lightSiderBg: t.bgElevated,
                lightTriggerBg: t.bgMuted,
                lightTriggerColor: t.fgMuted,
            },
            Tabs: {
                itemColor: t.fgMuted,
                itemHoverColor: t.fg,
                itemSelectedColor: t.primaryFg,
                itemActiveColor: t.primaryActive,
                // The one place the accent carries state in the panels: the rule under
                // the active tab. A hairline, never a fill. Gold once, then the pink,
                // now the brand coral — the restraint rule has outlived three hues.
                inkBarColor: t.accent,
                cardBg: t.bgSubtle,
                cardGutter: 4,
                cardPadding: `${spacing[2]} ${spacing[4]}`,
                horizontalItemGutter: 24,
                horizontalItemPadding: `${spacing[3]} 0`,
                titleFontSize: rem(typography.scale.body.fontSize),
                titleFontSizeLG: rem(typography.scale["title-sm"].fontSize),
            },
            Drawer: {
                colorBgElevated: t.bgElevated,
                footerPaddingBlock: px(spacing[4]),
                footerPaddingInline: px(spacing[6]),
                paddingLG: px(spacing[6]),
            },
            Alert: {
                borderRadiusLG: px(shape.card),
                defaultPadding: `${spacing[3]} ${spacing[4]}`,
                withDescriptionPadding: `${spacing[4]} ${spacing[5]}`,
                withDescriptionIconSize: 22,
                colorInfoBg: t.infoSoft,
                colorInfoBorder: t.infoBorder,
                colorSuccessBg: t.successSoft,
                colorSuccessBorder: t.successBorder,
                colorWarningBg: t.warningSoft,
                colorWarningBorder: t.warningBorder,
                colorErrorBg: t.dangerSoft,
                colorErrorBorder: t.dangerBorder,
            },
            Message: {
                contentBg: t.bgElevated,
                contentPadding: `${spacing[2.5]} ${spacing[4]}`,
            },
            Notification: {
                colorBgElevated: t.bgElevated,
                borderRadiusLG: px(shape.modal),
                paddingContentHorizontalLG: px(spacing[5]),
            },
            Tooltip: {
                // In light the tooltip ground is `fg` — an INVERTED ground, painted
                // from a foreground token. That is the same construct as the marketing
                // site's depth band, and it now has a token: `fgOnInk`, which is
                // contrast-tested against `fg` in both themes. It was spelled `t.bg`
                // here, which happened to be the same colour in light and was never
                // scored, because `fg` appeared in no surface list.
                colorBgSpotlight: dark ? t.bgMuted : t.fg,
                colorTextLightSolid: dark ? t.fg : t.fgOnInk,
                borderRadius: px(shape.input),
                controlHeight: 28,
                fontSize: rem(typography.scale["body-sm"].fontSize),
            },
            Popover: {
                colorBgElevated: t.bgElevated,
                borderRadiusLG: px(shape.modal),
                titleMinWidth: 160,
            },
            Dropdown: {
                // A danger menu item fills with `colorError` on hover, and that is the
                // only thing Dropdown uses `colorTextLightSolid` for.
                colorTextLightSolid: onDanger,
                colorBgElevated: t.bgElevated,
                borderRadiusLG: px(shape.modal),
                controlItemBgHover: t.bgHover,
                controlItemBgActive: t.primarySoft,
                controlItemBgActiveHover: t.primarySoftHover,
                paddingBlock: 4,
            },
            Segmented: {
                itemColor: t.fgMuted,
                itemHoverColor: t.fg,
                itemHoverBg: t.bgHover,
                itemSelectedBg: t.bgElevated,
                itemSelectedColor: t.primaryFg,
                trackBg: t.bgMuted,
                trackPadding: 3,
                borderRadius: px(shape.control),
                borderRadiusSM: px(shape.control),
            },
            Switch: {
                colorPrimary: t.primary,
                colorPrimaryHover: t.primaryHover,
                handleBg: t.bgElevated,
                trackHeight: 22,
                trackMinWidth: 42,
                handleSize: 18,
            },
            Checkbox: {
                borderRadiusSM: dark ? 4 : 2,
                colorPrimary: t.primary,
                colorPrimaryHover: t.primaryHover,
                colorBorder: t.borderStrong,
            },
            Radio: {
                colorPrimary: t.primary,
                colorBorder: t.borderStrong,
                buttonSolidCheckedBg: t.primary,
                buttonSolidCheckedHoverBg: t.primaryHover,
                buttonSolidCheckedActiveBg: t.primaryActive,
                buttonCheckedBg: t.primarySoft,
                buttonBg: t.bgElevated,
            },
            Slider: {
                railBg: t.bgInset,
                railHoverBg: t.bgActive,
                trackBg: t.primary,
                trackHoverBg: t.primaryHover,
                handleColor: t.primary,
                handleActiveColor: t.primaryActive,
                dotBorderColor: t.border,
                dotActiveBorderColor: t.primary,
            },
            Progress: {
                defaultColor: t.primary,
                remainingColor: t.bgInset,
                circleTextColor: t.fg,
            },
            Pagination: {
                itemBg: "transparent",
                itemActiveBg: t.primarySoft,
                itemLinkBg: "transparent",
                itemActiveColorDisabled: t.fgDisabled,
                itemSize: 32,
                itemSizeSM: 26,
                borderRadius: px(shape.control),
            },
            Steps: {
                colorPrimary: t.primary,
                // The Steps error glyph is deliberately NOT set here. It is fixed in
                // `packages/ui/src/styles/ui.css`, and the reason is structural rather
                // than a matter of taste: **Steps has two different error renderings
                // and only one of them is broken.**
                //
                // - `<Steps status="error">` — AntD derives the status, substitutes no
                //   icon, and paints a filled crimson circle with the glyph inside.
                //   That glyph comes from `colorTextLightSolid`, i.e. Ink. 2.47:1.
                // - `items: [{ status: 'error' }]` — the ui wrapper injects an icon, the
                //   step becomes `-custom`, and AntD then DROPS the fill and border and
                //   colours the glyph from `errorDotColor` instead. Already 6.64:1, and
                //   already on a contract-bound pair.
                //
                // A token here has no selector to hang that distinction on, so it would
                // have had to answer both — and answering the broken one would have
                // painted white-on-paper across the commoner custom-icon path. The CSS
                // rule carries `:not(.ant-steps-item-custom)` and can. It resolves
                // `var(--bk-on-danger)`, so it still inherits the AA guarantee
                // `a11y.test.ts` enforces on the onDanger/danger pair, and a test on
                // that side asserts AntD's default still fails so the override cannot be
                // quietly deleted.
                navArrowColor: t.fgSubtle,
                descriptionMaxWidth: 180,
                titleLineHeight: 24,
                iconSize: 30,
                iconFontSize: rem(typography.scale["body-sm"].fontSize),
            },
            Badge: {
                colorError: t.danger,
                // A default `<Badge count>` fills with `colorError`, not the primary,
                // so its digits take the danger role's on-colour. `badgeTextColor` is
                // an internal derived token rather than a public one, so the override
                // goes in through the global it derives from — which Badge uses for
                // nothing else.
                //
                // This is right for the DEFAULT ground and only that. A badge given an
                // explicit `<Badge color={…}>` has chosen a ground this file cannot
                // see, and one token cannot answer two grounds; such a call site owns
                // its own on-colour (the admin console's `.bk-badge-brand` is the
                // existing example). Do not "fix" that by flipping this value — it
                // would break the far commoner default.
                colorTextLightSolid: onDanger,
                textFontSize: rem(typography.scale["caption-sm"].fontSize),
                textFontSizeSM: rem(typography.scale["caption-sm"].fontSize),
                indicatorHeight: 18,
                indicatorHeightSM: 6,
                dotSize: 7,
            },
            Avatar: {
                // The fallback initials sit on `colorTextPlaceholder`; see
                // `onSubtleFill` for why this one value flips with the theme.
                colorTextLightSolid: onSubtleFill,
                containerSize: 36,
                containerSizeLG: 44,
                containerSizeSM: 28,
                groupBorderColor: t.bgElevated,
                colorTextPlaceholder: t.fgSubtle,
            },
            Descriptions: {
                labelBg: t.bgSubtle,
                titleMarginBottom: px(spacing[4]),
                itemPaddingBottom: px(spacing[3]),
                colorSplit: t.border,
            },
            Statistic: {
                titleFontSize: rem(typography.scale["body-sm"].fontSize),
                contentFontSize: rem(typography.scale["heading-sm"].fontSize),
                colorTextDescription: t.fgSubtle,
                // A Statistic is a number at heading size. Left to the global family it
                // would inherit Kalameh, which has no tnum and no uniform digit
                // advance, so a row of revenue cards would shuffle sideways on every
                // re-render. Pin it to the numeric stack.
                fontFamily: typography.fontFamily.numeric,
            },
            Divider: {
                colorSplit: t.border,
                textPaddingInline: px(spacing[3]),
                verticalMarginInline: px(spacing[2]),
            },
            Breadcrumb: {
                itemColor: t.fgSubtle,
                lastItemColor: t.fg,
                linkColor: t.fgSubtle,
                linkHoverColor: t.primaryFg,
                separatorColor: t.fgDisabled,
                separatorMargin: px(spacing[2]),
            },
            Collapse: {
                headerBg: t.bgSubtle,
                contentBg: t.bgElevated,
                headerPadding: `${spacing[3]} ${spacing[4]}`,
                contentPadding: `${spacing[4]}`,
                borderRadiusLG: px(shape.card),
            },
            List: {
                itemPadding: `${spacing[3]} 0`,
                itemPaddingLG: `${spacing[4]} ${spacing[6]}`,
                headerBg: "transparent",
                footerBg: "transparent",
                colorSplit: t.borderSubtle,
            },
            Timeline: {
                tailColor: t.border,
                dotBg: t.bgElevated,
                itemPaddingBottom: 22,
            },
            Empty: { colorTextDescription: t.fgSubtle },
            Result: { titleFontSize: rem(typography.scale.heading.fontSize) },
            Skeleton: {
                gradientFromColor: t.skeleton,
                gradientToColor: t.skeletonHighlight,
            },
            // The fullscreen spin tip sits on AntD's own dark scrim.
            Spin: {
                colorPrimary: t.primary,
                dotSize: 20,
                colorTextLightSolid: onForeignSolid,
            },
            // The image-preview toolbar sits on a black wash.
            Image: {
                previewOperationColor: onForeignSolid,
                previewOperationHoverColor: onForeignSolid,
                colorTextLightSolid: onForeignSolid,
            },
            Upload: {
                colorFillAlter: t.bgSubtle,
                colorBorder: t.borderStrong,
                actionsColor: t.fgSubtle,
                // The picture-card hover mask is a 50%-black wash — a foreign ground.
                colorTextLightSolid: onForeignSolid,
            },
            Calendar: {
                fullBg: t.bgElevated,
                fullPanelBg: t.bgElevated,
                itemActiveBg: t.primarySoft,
                colorSplit: t.borderSubtle,
            },
            /*
             * The only other place the accent appears: directory review stars.
             *
             * `starBg` is the EMPTY star, and it was `bgInset` — measured against the
             * page, 1.06:1 in light and 1.09:1 in dark. Both rating widgets in the
             * product are `disabled` displays of a score, so a three-star review
             * rendered as three coral stars floating in space with no five-star track
             * behind them: the numerator was legible and the denominator did not
             * exist. Filled-against-empty was 2.84:1 in light, under §1.4.11's 3:1
             * for the one distinction the control is making.
             *
             * `borderStrong` is the same hairline `Checkbox.colorBorder` and
             * `Radio.colorBorder` already use for an unchecked control, which is what
             * an empty star is. `contrastPairs` scores it against every surface, so
             * this is a pair the a11y contract already carries rather than a new one.
             *
             * `starSize` is on the ICON LADDER now, not AntD's 18. `packages/ui`'s
             * `Rate` draws the star from the Iconsax registry (AntD's default
             * `character` is `StarFilled` — a second icon set), and `<Icon>` writes
             * `width`/`height` attributes from a `16 | 20 | 24 | …` union, so an
             * off-ladder 18 is not expressible. The two must agree or the star and
             * its own row spacing drift apart.
             */
            Rate: { starColor: t.accent, starSize: 16, starBg: t.borderStrong },
            Anchor: { linkPaddingBlock: 6, colorPrimary: t.primary },
            Transfer: { listHeight: 220, headerHeight: 40, itemPaddingBlock: 6 },
            Splitter: { splitBarDraggableSize: 24, splitTriggerSize: 6 },
        },
    };
}
/** Light theme, ready to hand to `<ConfigProvider theme={…}>`. */
export const bookoraAntdLightTheme = bookoraAntdTheme("light");
/** Dark theme, ready to hand to `<ConfigProvider theme={…}>`. */
export const bookoraAntdDarkTheme = bookoraAntdTheme("dark");
export default bookoraAntdTheme;
//# sourceMappingURL=antd-theme.js.map